import React, { useRef } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import cx from 'classnames';

import { LANDING_PAGE_EVENTS } from 'Utilities/analytics';
import { SELL_MY_CAR_LANDING_PAGE_V2, SELL_YOUR_CAR_CITY_ROUTE } from 'Utilities/wordpress';

import StickyVRM from '../../../../components/misc/StickyVRM/StickyVRM';
import DialogLoader from '../../../../components/transitions/DialogLoader/DialogLoader';
import { createFAQData } from '../../common/FAQSection/FAQSection';
import { RoundedSection } from '../../common/RoundedSection/RoundedSection';
import Status404 from '../../error/404';
import FastlaneHero from '../../fastlane/home/Hero/Hero';
import USP from '../../fastlane/home/Usp/Usp';
import { MatchParams } from '../@types/landingPages';
import { ExpandableContentWrapper } from '../Components/ExpandableContentWrapper/ExpandableContentWrapper';
import { GuidesSection as Guides } from '../Components/GuidesSection/GuidesSection';
import HowItWorks from '../Components/HowItWorks/HowItWorks';
import { HowToSell } from '../Components/HowToSell/HowToSell';
import { LandingPagePartnerLogos } from '../Components/LandingPagePartnerLogos/LandingPagePartnerLogos';
import { RelatedGuidesSectionBespoke } from '../Components/RelatedGuidesSectionBespoke/RelatedGuidesSectionBespoke';
import { SEOSection } from '../Components/SEOSection/SEOSection';
import { Trustpilot } from '../Components/Trustpilot/Trustpilot';
import { useLandingPageData } from '../Hooks/useLandingPageData';

import { landingPageSectionLoadEvent, PAGE_SECTIONS } from './LandingPage.helpers';

import styles from './LandingPage.scss';

type HeroSectionProps = {
  description?: string;
  title?: string;
};

const fastlaneHero = ({ description, title }: HeroSectionProps) => (
  title && (<FastlaneHero
    analyticsEvents={{
      onLandingPageInputFocus: () => LANDING_PAGE_EVENTS.HERO_VRM_INPUT_FOCUSED(),
    }}
    isHideBreadcrumb={false}
    onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
    subTitle={description?.trim() || ''}
    {...{ title }}
  />
  ));

const SellMyCarLandingPageV2 = ({ description, title }: Omit<HeroSectionProps, 'hideBreadcrumb'>) => (
  <div className={cx(styles.component, styles.viewV2)}>
    <div className={styles.heroSection}>
      {fastlaneHero({ description, title }) }
      <USP onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)}/>
    </div>
    <HowItWorks />
    <Trustpilot />
  </div>
);

const LandingPage = () => {
  const $ref = useRef(null);
  const $content = $ref.current;
  const {
    acf,
    getRelatedGuidesContent: getGuidesContent,
    loading,
    stickyVRM,
  } = useLandingPageData();
  const { pathname } = useLocation();
  const { path: routePath } = useRouteMatch<MatchParams>();

  if (loading) {
    return (<DialogLoader />);
  }

  if (!acf) {
    return (<Status404 />);
  }

  const { header_description: fastlaneHeroDescription, header_title: fastlaneHeroTitle } = acf || {};

  // Testing a leaner version of the '/sell-my-car' landing page
  if (pathname === SELL_MY_CAR_LANDING_PAGE_V2) {
    return (
      <SellMyCarLandingPageV2
        description={fastlaneHeroDescription}
        title={fastlaneHeroTitle}
      />);
  }

  const { faq_title: faqTitle, related_guide_header: relatedGuidesTitle } = acf || {};
  const faq = createFAQData(acf);
  const isFAQEmpty = (!Array.isArray(faq) || !faq.length);

  const relatedGuides = getGuidesContent(acf);
  const isRelatedGuidesEmpty = (!Array.isArray(relatedGuides) || !relatedGuides.length);

  const isOnLocationPage = routePath === SELL_YOUR_CAR_CITY_ROUTE;

  // Trimmed content version of the landing page
  const isTrimmedContentVersion = (/\/sell-your-car\/.*/).test(pathname);

  return (
    <div ref={$ref} className={styles.component}>
      { stickyVRM && !!$content && !pathname.includes('/mileage') && (
        <StickyVRM
          {...{
            $content,
            mobileOnly: true,
          }}
        />
      )}

      <div className={styles.heroSection}>
        {fastlaneHero({
          description: fastlaneHeroDescription,
          title: fastlaneHeroTitle,
        })}
        <div className={cx(styles.heroSectionContentWrapper, {
          [styles.trimPadding]: isTrimmedContentVersion,
        })}>
          {!isTrimmedContentVersion && <RoundedSection withBottomLedge theme="off-white">
            <HowToSell {...{ acf }} />
          </RoundedSection> }
          <RoundedSection withBottomLedge withTopLedge>
            <LandingPagePartnerLogos />
          </RoundedSection>
        </div>
      </div>

      {acf?.section_three_body_area && <RoundedSection withBottomLedge withTopLedge theme="off-white">
        <SEOSection {...{ acf, isTrimmedContentVersion }} />
      </RoundedSection>}

      <RoundedSection withTopLedge theme="off-white">
        <Trustpilot wrapperClassName={styles.trustpilotSection} />
      </RoundedSection>
      {!isFAQEmpty && (
        <ExpandableContentWrapper
          {...{ faq, faqTitle }}
          onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.FAQ)}
        />
      )}
      {!isRelatedGuidesEmpty && (
        <RoundedSection withBottomLedge withTopLedge theme="off-white">
          <RelatedGuidesSectionBespoke
            guides={relatedGuides}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RELATED_GUIDES)}
            redirectToUrl={pathname}
            sectionTitle={relatedGuidesTitle}
          />
        </RoundedSection>
      )}
      {
        !isOnLocationPage && (
          <Guides />
        )
      }
    </div>
  );
};

export default LandingPage;
